import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { attendanceService, employeeService, authService, attendanceStructureService } from '../services/api';
import { useAttendanceCodeConfig } from '../context/attendanceCodeConfigContext';
import { AlertTriangle } from 'lucide-react';
import EmployeeAttendanceCalendarPopup from '../components/Admin/attendance/components/EmployeeAttendanceCalendarPopup';

const statusStyles = {
  'PR': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'WO': 'bg-gray-100 text-gray-800 border-gray-200',
  'PH': 'bg-purple-100 text-purple-800 border-purple-200',
  'HO': 'bg-purple-100 text-purple-800 border-purple-200',
  'CL': 'bg-amber-100 text-amber-800 border-amber-200',
  'EL': 'rh-accent-soft border-redhalo-teal/80',
  'LOP': 'bg-red-100 text-red-800 border-red-200',
  'KL': 'bg-amber-100 text-amber-800 border-amber-200',
  'ML': 'bg-sky-100 text-sky-800 border-sky-200',
  'BL': 'bg-slate-100 text-slate-800 border-slate-200',
  'CO': 'bg-orange-100 text-orange-800 border-orange-200',
  'CW': 'bg-teal-100 text-teal-800 border-teal-200',
};

// Default config codes (fallback when API not available)
const DEFAULT_LEGEND_CONFIGS = [
  { meaning: 'Present', originalCode: 'PR', customCode: 'PR' },
  { meaning: 'Loss of Pay', originalCode: 'LOP', customCode: 'LOP' },
  { meaning: 'Weekly Off', originalCode: 'WO', customCode: 'WO' },
  { meaning: 'Public Holiday', originalCode: 'PH', customCode: 'PH' },
];

export default function EmployeeAttendance() {
  const { mapping, reverseMapping } = useAttendanceCodeConfig();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceDetailData, setAttendanceDetailData] = useState({});
  const [attendanceLocks, setAttendanceLocks] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isLocked, setIsLocked] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [legendConfigs, setLegendConfigs] = useState([]);
  const [legendLeaveTypes, setLegendLeaveTypes] = useState([]);
  const [weeklyOffPolicies, setWeeklyOffPolicies] = useState([]);
  const [employeeWeeklyOffMappings, setEmployeeWeeklyOffMappings] = useState({});
  const [holidaysData, setHolidaysData] = useState([]);
  const [holidayPolicies, setHolidayPolicies] = useState([]);
  const [employeeHolidayMappings, setEmployeeHolidayMappings] = useState({});

  // Fetch admin-configured default codes (Configuration) and leave types (Leave Management) for legend
  useEffect(() => {
    const loadLegendData = async () => {
      try {
        const [configs, ltResp] = await Promise.all([
          attendanceService.getConfigurations().catch(() => []),
          attendanceStructureService.getLeaveTypes().catch(() => ({ success: false, data: [] })),
        ]);
        setLegendConfigs(Array.isArray(configs) && configs.length > 0 ? configs : DEFAULT_LEGEND_CONFIGS);
        setLegendLeaveTypes(Array.isArray(ltResp?.data) ? ltResp.data : []);
      } catch (e) {
        console.warn('Failed to load legend data:', e);
        setLegendConfigs(DEFAULT_LEGEND_CONFIGS);
        setLegendLeaveTypes([]);
      }
    };
    loadLegendData();
  }, []);

  // Fetch employee profile to get joining date (use only employeeId – works for any logged-in employee)
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        const currentUserResponse = await authService.getCurrentUser();
        if (currentUserResponse && currentUserResponse.data) {
          const userData = currentUserResponse.data;
          const employeeId = userData.employeeId;
          if (employeeId) {
            const employeeResponse = await employeeService.getEmployee(employeeId);
            if (employeeResponse && employeeResponse.data) {
              setEmployee(employeeResponse.data);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch employee data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeData();
  }, []);

  // Fetch attendance locks
  useEffect(() => {
    const loadLocks = async () => {
      try {
        const response = await attendanceStructureService.getAttendanceLocks();
        if (response.success && Array.isArray(response.data)) {
          setAttendanceLocks(response.data || []);
          const currentLock = response.data.find(
            (lock) => lock.month === selectedMonth + 1 && lock.year === selectedYear
          );
          setIsLocked(!!currentLock);
        } else {
          setAttendanceLocks([]);
          setIsLocked(false);
        }
      } catch (err) {
        console.error('Failed to load attendance locks:', err);
        setAttendanceLocks([]);
        setIsLocked(false);
      }
    };
    loadLocks();
  }, [selectedMonth, selectedYear]);

  // Fetch weekly off, holidays and policy mappings (for default PR/WO/PH when locked and backend is empty)
  useEffect(() => {
    const loadPolicies = async () => {
      try {
        const [woResp, woMapResp, holidaysResp, hpResp, hpMapResp] = await Promise.all([
          attendanceStructureService.getWeeklyOffPolicies().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getEmployeeWeeklyOffPolicies().catch(() => ({ success: false, data: {} })),
          attendanceStructureService.getHolidays().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getHolidayPolicies().catch(() => ({ success: false, data: [] })),
          attendanceStructureService.getEmployeeHolidayPolicies().catch(() => ({ success: false, data: {} })),
        ]);
        setWeeklyOffPolicies(woResp?.data || []);
        setEmployeeWeeklyOffMappings(woMapResp?.data || {});
        setHolidaysData(holidaysResp?.data || []);
        setHolidayPolicies(hpResp?.data || []);
        setEmployeeHolidayMappings(hpMapResp?.data || {});
      } catch (e) {
        console.warn('Failed to load attendance policies:', e);
      }
    };
    loadPolicies();
  }, []);

  // Fetch attendance data for selected month (always fetch so employee sees data like admin view)
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!employee) {
        setAttendanceData({});
        return;
      }

      try {
        setLoadingAttendance(true);
        const response = await attendanceService.getMyAttendanceHistory(
          selectedMonth + 1, // API expects 1-12
          selectedYear
        );

        if (response.success && response.data && Array.isArray(response.data)) {
          const attendanceMap = {};
          const detailMap = {};
          response.data.forEach((record) => {
            const raw = record.date;
            if (!raw || typeof raw !== 'string') return;
            const parts = raw.trim().split(/[-/]/);
            if (parts.length < 3) return;
            const day = parseInt(parts[2], 10);
            if (Number.isNaN(day) || day < 1 || day > 31) return;
            const dayKey = String(day);
            attendanceMap[day] = record.status || '-';
            detailMap[dayKey] = {
              status: record.status || '-',
              checkIn:
                record.checkIn ||
                record.checkInTime ||
                record.inTime ||
                record.firstIn ||
                record.firstPunchIn ||
                '--',
              checkOut:
                record.checkOut ||
                record.checkOutTime ||
                record.outTime ||
                record.lastOut ||
                record.lastPunchOut ||
                '--',
              totalHours:
                record.totalHours ||
                record.workedHours ||
                record.workingHours ||
                record.totalWorkedHours ||
                '--',
              shiftAssigned:
                record.shiftAssigned ||
                record.shiftName ||
                record.shift ||
                record.assignedShift ||
                '--',
            };
          });
          setAttendanceData(attendanceMap);
          setAttendanceDetailData(detailMap);
        } else {
          setAttendanceData({});
          setAttendanceDetailData({});
        }
      } catch (error) {
        console.error('Failed to fetch attendance:', error);
        setAttendanceData({});
      } finally {
        setLoadingAttendance(false);
      }
    };

    fetchAttendance();
  }, [employee, selectedMonth, selectedYear]);

  const today = new Date();
  const todayNum = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

  // Check if a date is in the future (date-only comparison, no time)
  const isFutureDate = useCallback((day) => {
    const cellNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
    return cellNum > todayNum;
  }, [selectedYear, selectedMonth, todayNum]);

  // When locked: compute default PR/WO/PH for days with no backend data (same as admin)
  const effectiveAttendance = useMemo(() => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const result = { ...attendanceData };
    if (!employee) return result;

    const joinDate = employee.joiningDate || employee.joinDate;
    const relievedDate = employee.relievedDate || employee.exitDate || employee.relievingDate;
    let joinNum = 0;
    let relieveNum = 99999999;
    if (joinDate) {
      const d = new Date(joinDate);
      if (!isNaN(d.getTime())) joinNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    if (relievedDate) {
      const d = new Date(relievedDate);
      if (!isNaN(d.getTime())) relieveNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }

    const isPolicyEffectiveOnDate = (policy, date) => {
      if (!policy?.effectiveDate) return true;
      const ed = new Date(policy.effectiveDate);
      return !isNaN(ed.getTime()) && ed <= date;
    };

    const empId = employee.id || employee.employeeId || employee.empCode;
    const woMapping = employeeWeeklyOffMappings[empId] || employeeWeeklyOffMappings[String(empId)];
    const policyIds = woMapping ? (Array.isArray(woMapping) ? woMapping : woMapping.policies || (woMapping.policyId != null ? [woMapping.policyId] : [])) : [];

    const isWeeklyOff = (date) => {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
      let policy = weeklyOffPolicies.find((p) => policyIds.includes(p.id));
      if (!policy) policy = weeklyOffPolicies.find((p) => isPolicyEffectiveOnDate(p, date) && p.weeklyOffs?.[dayName]?.enabled);
      const wo = policy?.weeklyOffs?.[dayName];
      return !!(wo && wo.enabled);
    };

    const dateString = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const isHoliday = (date) => {
      const ds = date.toISOString().split('T')[0];
      if ((holidaysData || []).some((h) => h.date === ds && h.isGlobal)) return true;
      const empPolicyIds = employeeHolidayMappings[empId] || employeeHolidayMappings[String(empId)] || [];
      const policies = (holidayPolicies || []).filter((p) => empPolicyIds.includes(p.id));
      return policies.some((p) => (p.holidays || []).some((h) => (h.date || h) === ds || (typeof h === 'object' && h.date === ds)));
    };

    for (let day = 1; day <= daysInMonth; day++) {
      const currentNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
      if (currentNum < joinNum || currentNum > relieveNum) continue;
      if (result[day] && result[day] !== '-') continue;
      const date = new Date(selectedYear, selectedMonth, day);
      if (isHoliday(date)) result[day] = 'PH';
      else if (isWeeklyOff(date)) result[day] = 'WO';
      /* PR/LOP for working days comes from punch + policy engine in calendar — not defaulted here */
    }
    return result;
  }, [employee, attendanceData, selectedMonth, selectedYear, weeklyOffPolicies, employeeWeeklyOffMappings, holidaysData, holidayPolicies, employeeHolidayMappings]);

  const popupAttendanceData = useMemo(() => {
    const empId = String(employee?.id || employee?.employeeId || '');
    if (!empId) return {};
    return {
      [empId]: Object.fromEntries(
        Object.entries(effectiveAttendance || {}).map(([day, status]) => [String(day), status || '-'])
      ),
    };
  }, [employee?.id, employee?.employeeId, effectiveAttendance]);

  const popupAttendanceDetailData = useMemo(() => {
    const empId = String(employee?.id || employee?.employeeId || '');
    if (!empId) return {};
    const detailMap = { ...attendanceDetailData };
    Object.entries(effectiveAttendance || {}).forEach(([day, status]) => {
      if (!detailMap[String(day)]) {
        detailMap[String(day)] = {
          status: status || '-',
          checkIn: '--',
          checkOut: '--',
          totalHours: '--',
          shiftAssigned: '--',
        };
      }
    });
    return { [empId]: detailMap };
  }, [employee?.id, employee?.employeeId, effectiveAttendance, attendanceDetailData]);

  const popupEmployee = useMemo(() => {
    if (!employee) return null;
    return {
      ...employee,
      id: employee.id || employee.employeeId,
      code: employee.code || employee.empCode || employee.employeeId,
      name: employee.name || employee.employeeName,
    };
  }, [employee]);

  const handlePopupStatusEdit = useCallback((emp, day, combinedStatus) => {
    if (!day) return;
    setAttendanceData((prev) => ({ ...prev, [day]: combinedStatus }));
  }, []);

  // Paid/unpaid leave codes for pay-days calculation (same logic as admin)
  const { unpaidLeaveCodes, paidLeaveCodes } = useMemo(() => {
    const unpaid = new Set(['LOP']);
    const paid = new Set(['PR', 'PH', 'WO']);
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || '').trim().toUpperCase();
      if (!code) return;
      if (lt.isPaid === false || lt.is_paid === false) unpaid.add(code);
      else paid.add(code);
    });
    return { unpaidLeaveCodes: unpaid, paidLeaveCodes: paid };
  }, [legendLeaveTypes]);

  // Known status codes for splitting combined statuses (e.g. PRCL -> PR + CL)
  const knownStatusCodes = useMemo(() => {
    const set = new Set(['PR', 'PH', 'WO', 'CL', 'EL', 'CO', 'CW', 'LOP']);
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || lt.shortCode || '').trim().toUpperCase();
      if (code) set.add(code);
    });
    return set;
  }, [legendLeaveTypes]);

  // Status counts for the selected month (uses effectiveAttendance so when locked PR/WO/PH are counted)
  const statusCounts = useMemo(() => {
    const counts = { PR: 0, PH: 0, WO: 0, CL: 0, EL: 0, CO: 0, CW: 0, LOP: 0 };
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    let joinDateNum = 0;
    let relievedDateNum = 99999999;
    if (employee?.joiningDate) {
      const d = new Date(employee.joiningDate);
      if (!isNaN(d.getTime())) joinDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    const relieved = employee?.relievedDate || employee?.exitDate || employee?.relievingDate;
    if (relieved) {
      const d = new Date(relieved);
      if (!isNaN(d.getTime())) relievedDateNum = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
    const normalize = (s) => String(s || '').trim().toUpperCase();
    for (let day = 1; day <= daysInMonth; day++) {
      const currentNum = selectedYear * 10000 + (selectedMonth + 1) * 100 + day;
      if (currentNum < joinDateNum || currentNum > relievedDateNum) continue;
      let status = effectiveAttendance[day];
      if (!status || status === '-') continue;
      status = normalize(status);
      // Combined status (e.g. PRCL, LWPPR)
      const trySplit = (str) => {
        if (str.length <= 3 && knownStatusCodes.has(str)) return [str];
        for (let len = 3; len >= 2; len--) {
          const first = str.substring(0, len);
          const rest = str.substring(len);
          if (knownStatusCodes.has(first) && rest && knownStatusCodes.has(rest)) return [first, rest];
        }
        return [str];
      };
      const parts = trySplit(status);
      if (parts.length >= 2) {
        parts.forEach((p) => {
          const key = normalize(p);
          if (counts[key] === undefined) counts[key] = 0;
          counts[key] += 0.5;
        });
      } else {
        const key = parts[0] ? normalize(parts[0]) : status;
        if (counts[key] === undefined) counts[key] = 0;
        counts[key] += 1;
      }
    }
    return counts;
  }, [effectiveAttendance, selectedMonth, selectedYear, employee, knownStatusCodes]);

  // Pay days = sum of paid status counts (same logic as admin)
  const payDays = useMemo(() => {
    let total = 0;
    Object.entries(statusCounts || {}).forEach(([code, val]) => {
      const c = Number(val) || 0;
      if (c === 0) return;
      const upper = String(code).toUpperCase();
      if (unpaidLeaveCodes && unpaidLeaveCodes.has(upper)) return;
      if (upper === 'LOP') return;
      if (paidLeaveCodes && paidLeaveCodes.has(upper)) total += c;
      else total += c; // unknown code treat as paid
    });
    return total;
  }, [statusCounts, paidLeaveCodes, unpaidLeaveCodes]);

  // Totals object for the Totals bar (like admin "Calculate and Show Summary")
  const totals = useMemo(() => ({
    ...statusCounts,
    payDays,
  }), [statusCounts, payDays]);

  // Selectable codes only: from attendance config + leave types (same as admin dropdown). No hardcoding.
  const selectableCodesOrdered = useMemo(() => {
    const fromConfig = (legendConfigs || [])
      .filter((c) => c.customCode || c.originalCode || c.original_code)
      .map((c) => String(c.customCode || c.originalCode || c.original_code || '').toUpperCase())
      .filter(Boolean);
    const configSet = new Set(fromConfig);
    const fromLeave = (legendLeaveTypes || [])
      .filter((lt) => lt.code && (lt.isActive !== false && lt.is_active !== false))
      .map((lt) => String(lt.code || '').toUpperCase())
      .filter((code) => code && !configSet.has(code));
    return [...fromConfig, ...fromLeave];
  }, [legendConfigs, legendLeaveTypes]);

  // Full form per code: from config.meaning or leaveType.name only (no hardcoded map)
  const fullFormByCode = useMemo(() => {
    const map = { payDays: 'Pay Days' };
    (legendConfigs || []).forEach((c) => {
      const code = String(c.customCode || c.originalCode || c.original_code || '').toUpperCase();
      if (code && (c.meaning || c.meaning === 0)) map[code] = c.meaning;
    });
    (legendLeaveTypes || []).forEach((lt) => {
      const code = String(lt.code || '').toUpperCase();
      if (code && (lt.name || lt.name === 0)) map[code] = lt.name;
    });
    return map;
  }, [legendConfigs, legendLeaveTypes]);

  const getFullForm = useCallback((code) => {
    if (code === 'payDays') return fullFormByCode.payDays || 'Pay Days';
    const upper = String(code || '').toUpperCase();
    return fullFormByCode[upper] ?? (upper || code);
  }, [fullFormByCode]);

  // Legend entries: only selectable codes + payDays, each with full form from API (config/leave types)
  const legendEntries = useMemo(() => {
    const keys = [...selectableCodesOrdered, 'payDays'];
    return keys.map((code) => {
      const label = getFullForm(code);
      const upper = code === 'payDays' ? code : String(code).toUpperCase();
      const style = code === 'payDays'
        ? 'rh-accent-soft border-redhalo-teal/60'
        : (statusStyles[upper] || 'bg-gray-100 text-gray-800 border-gray-200');
      const leaveType = legendLeaveTypes?.find((lt) => String(lt.code || '').toUpperCase() === upper);
      const color = leaveType?.color ? (leaveType.color.startsWith('#') ? leaveType.color : `#${leaveType.color}`) : null;
      return { code: upper === code ? code : upper, label, style, color, isPayDays: code === 'payDays' };
    });
  }, [selectableCodesOrdered, getFullForm, legendLeaveTypes]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-8xl h-full bg-white rounded-lg shadow-md overflow-hidden">
        <div className="h-[74.1vh] min-h-[589px] p-[1.425rem]">
          {popupEmployee && (
            <EmployeeAttendanceCalendarPopup
              embedded
              darkMode={false}
              isOpen
              onClose={() => {}}
              employee={popupEmployee}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              attendanceData={popupAttendanceData}
              attendanceDetailData={popupAttendanceDetailData}
              leaveTypes={legendLeaveTypes}
              isLocked={isLocked}
              onEditAttendanceStatus={handlePopupStatusEdit}
              onSaveRegularizationTimes={() => {}}
              onApplyLeave={() => {}}
            />
          )}
        </div>
      </div>
    </div>
  );
}
